import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation, useSearchParams } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { RouteTracker } from '@/components/RouteTracker';
import { useSession } from '@/lib/auth';
import { Login } from '@/pages/Login';
import { Signup } from '@/pages/Signup';
import { Verify } from '@/pages/Verify';
import { ForgotPassword } from '@/pages/ForgotPassword';
import { ResetPassword } from '@/pages/ResetPassword';
import { Dashboard } from '@/pages/Dashboard';
import { Numbers } from '@/pages/Numbers';
import { Projects } from '@/pages/Projects';
import { Inbox } from '@/pages/Inbox';
import { Contacts } from '@/pages/Contacts';
import { Taxonomies } from '@/pages/Taxonomies';
import { Suppressions } from '@/pages/Suppressions';
import { Account } from '@/pages/Account';
import { Keys } from '@/pages/Keys';
import { Webhooks } from '@/pages/Webhooks';
import { Widgets } from '@/pages/Widgets';
import { Campaigns } from '@/pages/Campaigns';
import { Templates } from '@/pages/Templates';
import { Billing } from '@/pages/Billing';
import { Support } from '@/pages/Support';
import { Ajuda } from '@/pages/Ajuda';
import { AdminPlans } from '@/pages/AdminPlans';
import { PlatformFinance } from '@/pages/PlatformFinance';
import { PlatformTenants } from '@/pages/PlatformTenants';
import { PlatformTenantDetail } from '@/pages/PlatformTenantDetail';
import { PlatformHealth } from '@/pages/PlatformHealth';
import { PlatformStripe } from '@/pages/PlatformStripe';
import { PlatformPartners } from '@/pages/PlatformPartners';
import { ConnectedApps } from '@/pages/ConnectedApps';
import { ConectarIA } from '@/pages/ConectarIA';
import { Playground } from '@/pages/Playground';
import { SettingsPage } from '@/pages/Settings';

function Guard({ children }: { children: React.ReactNode }) {
  const { token } = useSession();
  const { pathname, search } = useLocation();
  if (token) return <>{children}</>;
  // Guarda para onde a pessoa ia: depois do login ela volta ao lugar certo.
  const next = pathname && pathname !== '/' ? `?next=${encodeURIComponent(pathname + search)}` : '';
  return <Navigate to={`/login${next}`} replace />;
}

function LoginRoute() {
  const { token } = useSession();
  const [params] = useSearchParams();
  if (token) return <Navigate to="/" replace />;
  // Compat: links antigos do site (?signup=1) caem direto no onboarding.
  if (params.has('signup')) return <Navigate to="/signup" replace />;
  return <Login />;
}

function SignupRoute() {
  const { token } = useSession();
  return token ? <Navigate to="/" replace /> : <Signup />;
}

// Layout pathless no topo: monta o <RouteTracker/> (GA4 page_view a cada navegação,
// inclusive nas telas de login/signup, que fazem parte da jornada) e renderiza o Outlet.
const router = createBrowserRouter([
  {
    element: (
      <>
        <RouteTracker />
        <Outlet />
      </>
    ),
    children: [
      { path: '/login', element: <LoginRoute /> },
      { path: '/signup', element: <SignupRoute /> },
      { path: '/verify', element: <Verify /> },
      { path: '/forgot', element: <ForgotPassword /> },
      { path: '/reset', element: <ResetPassword /> },
      // Consentimento do conector de IA (OAuth): pede login, mas fica fora do AppShell —
      // é uma decisão de uma tela só, sem menu, como as telas de autorização de terceiros.
      { path: '/conectar-ia', element: <Guard><ConectarIA /></Guard> },
      {
        path: '/',
        element: (
          <Guard>
            <AppShell />
          </Guard>
        ),
        children: [
          { index: true, element: <Dashboard /> },
          { path: 'numbers', element: <Numbers /> },
          { path: 'projects', element: <Projects /> },
          { path: 'inbox', element: <Inbox /> },
          { path: 'contacts', element: <Contacts /> },
          { path: 'contacts/taxonomies', element: <Taxonomies /> },
          { path: 'contacts/blocklist', element: <Suppressions /> },
          { path: 'keys', element: <Keys /> },
          { path: 'webhooks', element: <Webhooks /> },
          { path: 'campaigns', element: <Campaigns /> },
          { path: 'templates', element: <Templates /> },
          { path: 'widgets', element: <Widgets /> },
          { path: 'usage', element: <Navigate to="/" replace /> },
          { path: 'playground', element: <Playground /> },
          { path: 'account', element: <Account /> },
          { path: 'billing', element: <Billing /> },
          { path: 'connected-apps', element: <ConnectedApps /> },
          { path: 'settings', element: <SettingsPage /> },
          { path: 'support', element: <Support /> },
          { path: 'ajuda', element: <Ajuda /> },
          { path: 'platform/plans', element: <AdminPlans /> },
          { path: 'platform/finance', element: <PlatformFinance /> },
          { path: 'platform/health', element: <PlatformHealth /> },
          { path: 'platform/tenants', element: <PlatformTenants /> },
          { path: 'platform/tenants/:id', element: <PlatformTenantDetail /> },
          { path: 'platform/stripe', element: <PlatformStripe /> },
          { path: 'platform/partners', element: <PlatformPartners /> },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
